import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Moon, Sparkles, Sun, Sunset, HelpCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

import { trackHormonalProfileCompleted } from '@/lib/mixpanelProgressionEvents';

interface OnboardingSlideCyclePhaseProps {
  onNext: () => void;
  isCompleting?: boolean;
  cycleType?: 'regular' | 'irregular';
}

type CyclePhase = 'menstrual' | 'follicular' | 'ovulation' | 'luteal';
type IrregularPhase = 'on_period' | 'not_on_period' | 'not_sure';

const regularPhaseOptions = [
  {
    value: 'menstrual' as CyclePhase,
    label: 'Menstrual (Days 1-5)',
    icon: Moon,
    description: 'Your period - focus on calming inflammation',
  },
  {
    value: 'follicular' as CyclePhase,
    label: 'Follicular (Days 6-14)',
    icon: Sun,
    description: 'Pre-ovulation - skin tolerance is highest',
  },
  {
    value: 'ovulation' as CyclePhase,
    label: 'Ovulation (Days 14-16)',
    icon: Sparkles,
    description: 'Peak glow - maintain your routine',
  },
  {
    value: 'luteal' as CyclePhase,
    label: 'Luteal (Days 17-28)',
    icon: Sunset,
    description: 'Pre-period - manage increased oil & breakouts',
  },
];

const irregularPhaseOptions = [
  {
    value: 'on_period' as IrregularPhase,
    label: "I'm currently on my period",
    icon: Moon,
    description: 'Focus on gentle, soothing care',
  },
  {
    value: 'not_on_period' as IrregularPhase,
    label: "I'm not on my period",
    icon: Sun,
    description: "We'll adjust based on your skin's current state",
  },
  {
    value: 'not_sure' as IrregularPhase,
    label: "I'm not sure",
    icon: HelpCircle,
    description: 'Tracking is difficult - that\'s okay',
  },
];

export const OnboardingSlideCyclePhase = ({ 
  onNext, 
  isCompleting,
  cycleType = 'regular'
}: OnboardingSlideCyclePhaseProps) => {
  const [selectedPhase, setSelectedPhase] = useState<CyclePhase | IrregularPhase | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const isRegular = cycleType === 'regular';
  const options = isRegular ? regularPhaseOptions : irregularPhaseOptions;

  // Map irregular phases to database values
  const mapPhaseToDbValue = (phase: CyclePhase | IrregularPhase): string => {
    if (isRegular) return phase;
    // Map irregular phases
    switch (phase) {
      case 'on_period':
        return 'menstrual';
      case 'not_on_period':
        return 'follicular'; // Default to follicular when not on period
      case 'not_sure':
        return 'unknown';
      default:
        return phase;
    }
  };

  const handleContinue = async () => {
    if (!selectedPhase) {
      toast.error('Please select your current cycle phase');
      return;
    }

    setIsSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        // User not authenticated yet, just continue to next slide
        // They can update cycle phase later in Settings
        onNext();
        return;
      }

      const dbPhase = mapPhaseToDbValue(selectedPhase);

      const { error } = await supabase
        .from('profiles')
        .update({
          cycle_phase: dbPhase,
          hormonal_data_updated_at: new Date().toISOString(),
        })
        .eq('id', user.id);

      if (error) throw error;

      // Track completion with cycle phase
      const { data: profile } = await supabase
        .from('profiles')
        .select('hormonal_status')
        .eq('id', user.id)
        .maybeSingle();
      
      if (profile?.hormonal_status) {
        trackHormonalProfileCompleted(profile.hormonal_status, dbPhase);
      }

      toast.success('Cycle phase saved');
      onNext();
    } catch (error) {
      console.error('Error saving cycle phase:', error);
      toast.error('Failed to save cycle phase');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSkip = () => {
    onNext();
  };

  return (
    <Card className="shadow-card border-0 bg-gradient-card">
      <CardContent className="p-6 md:p-8 flex flex-col">
        <div className="text-center space-y-3 mb-6">
          <div className="w-16 h-16 rounded-full bg-gradient-primary flex items-center justify-center mb-3 shadow-glow animate-scale-in mx-auto">
            <Moon className="w-8 h-8 text-primary-foreground" />
          </div>
          <h2 className="text-xl md:text-2xl font-semibold animate-fade-in">
            {isRegular 
              ? 'Track your cycle for smarter recommendations'
              : 'Approximate where you are in your cycle'
            }
          </h2>
          <p className="text-sm text-muted-foreground max-w-xl mx-auto animate-fade-in" style={{ animationDelay: '0.2s' }}>
            {isRegular
              ? 'Your skin changes throughout your cycle. Tell us where you are today.'
              : "We know irregular cycles are hard to track. Give us your best estimate."
            }
          </p>
        </div>

        <div className="space-y-3 mb-6">
          {options.map((option, index) => {
            const Icon = option.icon;
            return (
              <button
                key={option.value}
                onClick={() => setSelectedPhase(option.value)}
                className={`w-full p-4 rounded-lg border-2 transition-all text-left animate-fade-in ${
                  selectedPhase === option.value
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/50 bg-background'
                }`}
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-lg ${
                    selectedPhase === option.value ? 'bg-primary text-primary-foreground' : 'bg-muted'
                  }`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-sm mb-1">{option.label}</h3>
                    <p className="text-xs text-muted-foreground">{option.description}</p>
                  </div>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    selectedPhase === option.value
                      ? 'border-primary bg-primary'
                      : 'border-muted-foreground/30'
                  }`}>
                    {selectedPhase === option.value && (
                      <div className="w-2 h-2 bg-white rounded-full" />
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <div className="text-center mb-4">
          <p className="text-xs text-muted-foreground">
            Not sure? You can update this anytime in Settings
          </p>
        </div>

        <div className="flex flex-col gap-2">
          <Button
            onClick={handleContinue}
            disabled={!selectedPhase || isSaving || isCompleting}
            className="w-full bg-gradient-primary hover:opacity-90"
            size="lg"
          >
            {isSaving ? 'Saving...' : 'Continue'}
          </Button>
          <Button
            onClick={handleSkip}
            disabled={isSaving || isCompleting}
            variant="ghost"
            className="w-full"
          >
            Skip for now
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
